import { useParams, Link } from "react-router-dom";
import { useEffect, useState } from "react";
import { getProjectBySlug, getProjects } from "./services/api";
import { motion } from "framer-motion";

export default function ProjectDetail() {
  const { slug } = useParams();

  const [project, setProject] = useState(null);
  const [allProjects, setAllProjects] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    window.scrollTo(0, 0);

    Promise.all([
      getProjectBySlug(slug),
      getProjects()
    ])
      .then(([projectRes, allRes]) => {
        setProject(projectRes.data);
        setAllProjects(allRes.data);
        setLoading(false);
      })
      .catch((err) => {
        console.error(err);
        setLoading(false);
      });

  }, [slug]);

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-slate-500 text-lg animate-pulse">
          Loading project...
        </div>
      </div>
    );
  }

  if (!project) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center text-slate-500">
        Project not found.
      </div>
    );
  }

  const currentIndex = allProjects.findIndex(
    (p) => p.slug === project.slug
  );

  const prevProject =
    currentIndex > 0 ? allProjects[currentIndex - 1] : null;

  const nextProject =
    currentIndex < allProjects.length - 1
      ? allProjects[currentIndex + 1]
      : null;

  return (
    <section className="relative py-32 bg-slate-50 overflow-hidden">

      {/* Atmosphere Layer */}
      <div className="absolute -top-40 left-1/2 -translate-x-1/2 w-[700px] h-[400px] bg-blue-100/40 blur-3xl"></div>

      <motion.div
        initial={{ opacity: 0, y: 40 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
        className="relative max-w-4xl mx-auto px-6 md:px-12"
      >

        {/* Back Button */}
        <Link
          to="/projects"
          className="inline-block mb-16 text-slate-500 hover:text-blue-600 transition text-sm font-medium"
        >
          ← Back to Projects
        </Link>

        {/* Title */}
        <h1 className="text-4xl md:text-5xl font-semibold tracking-tight text-slate-900 mb-12">
          {project.title}
        </h1>

        {/* Hero Image */}
        {project.image && (
          <div className="overflow-hidden rounded-3xl mb-20 shadow-sm border border-slate-200">
            <img
              src={project.image}
              alt={project.title}
              className="w-full transition duration-700 hover:scale-[1.02]"
            />
          </div>
        )}

        {/* Content Sections */}
        <div className="space-y-20">

          {project.problem_statement && (
            <Section title="Problem Statement">
              {project.problem_statement}
            </Section>
          )}

          {project.architecture && (
            <section>
              <h2 className="text-2xl font-semibold text-slate-900 mb-6">
                Architecture
              </h2>
              <pre className="bg-slate-900 text-slate-200 p-6 rounded-2xl text-sm whitespace-pre-wrap overflow-x-auto">
                {project.architecture}
              </pre>
            </section>
          )}

          {project.challenges && (
            <SectionList
              title="Challenges"
              items={project.challenges.split("\n")}
            />
          )}

          {project.solutions && (
            <SectionList
              title="Solutions"
              items={project.solutions.split(",")}
            />
          )}

          {project.impact && (
            <SectionList
              title="Impact"
              items={project.impact.split(",")}
              highlight
            />
          )}

          {project.tech_stack && (
            <section>
              <h2 className="text-2xl font-semibold text-slate-900 mb-6">
                Tech Stack
              </h2>
              <div className="flex flex-wrap gap-3">
                {project.tech_stack.split(",").map((tech, index) => (
                  <span
                    key={index}
                    className="bg-white border border-slate-200 px-4 py-2 rounded-full text-sm text-slate-700"
                  >
                    {tech.trim()}
                  </span>
                ))}
              </div>
            </section>
          )}

        </div>

        {/* Links */}
        <div className="flex flex-wrap gap-6 mt-20">
          {project.github_url && (
            <a
              href={project.github_url}
              target="_blank"
              rel="noreferrer"
              className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-2xl text-sm font-medium transition"
            >
              View Code
            </a>
          )}

          {project.live_url && (
            <a
              href={project.live_url}
              target="_blank"
              rel="noreferrer"
              className="border border-slate-300 px-6 py-3 rounded-2xl text-sm font-medium hover:bg-slate-100 transition"
            >
              Live Demo
            </a>
          )}
        </div>

        {/* Divider */}
        <div className="border-t border-slate-200 my-24"></div>

        {/* Prev / Next */}
        <div className="flex justify-between items-center text-sm font-medium">

          {prevProject ? (
            <Link
              to={`/projects/${prevProject.slug}`}
              className="text-slate-500 hover:text-blue-600 transition"
            >
              ← {prevProject.title}
            </Link>
          ) : <div />}

          {nextProject && (
            <Link
              to={`/projects/${nextProject.slug}`}
              className="text-slate-500 hover:text-blue-600 transition text-right"
            >
              {nextProject.title} →
            </Link>
          )}

        </div>

      </motion.div>
    </section>
  );
}

function Section({ title, children }) {
  return (
    <section>
      <h2 className="text-2xl font-semibold text-slate-900 mb-6">
        {title}
      </h2>
      <p className="text-slate-600 leading-relaxed text-lg">
        {children}
      </p>
    </section>
  );
}

function SectionList({ title, items, highlight = false }) {
  return (
    <section>
      <h2 className="text-2xl font-semibold text-slate-900 mb-6">
        {title}
      </h2>
      <ul className="space-y-4 text-slate-600">
        {items.map((item, i) => (
          <li key={i} className={highlight ? "text-blue-600 font-medium" : ""}>
            • {item.trim()}
          </li>
        ))}
      </ul>
    </section>
  );
}